import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import type {MessengerPresencePort} from './presence.port';
import {MemberStatus, MessengerMemberRef} from './messenger.models';

/**
 * The presence port on the server. B-15 has not shipped (ITP-P-33 Release 1): today's `UserStatus` is partial and is
 * not fanned out to a conversation's members. What the client already holds is the contacts store's last-seen
 * dictionary (fed by the last-seen fetch and the online-activity notifications), so this adapter starts
 * there (ITP-E-194-09): online or offline with the last activity, nothing for a member it has never seen. Away, busy,
 * do not disturb and the status message come with B-15; until then its flag keeps the dots dark in production.
 */
@Injectable({providedIn: 'root'})
export class MessengerPresenceReal implements MessengerPresencePort {
  constructor(private store: Store<any>) {
  }

  statuses(members: MessengerMemberRef[]): Observable<Record<string, MemberStatus>> {
    const emails = members.map(member => member.email.toLowerCase());
    return this.store.select(state => state?.contacts?.lastSeen).pipe(
      distinctUntilChanged(),
      map(lastSeen => {
        const byEmail: Record<string, any> = {};
        Object.keys(lastSeen || {}).forEach(email => byEmail[email.toLowerCase()] = lastSeen[email]);
        const statuses: Record<string, MemberStatus> = {};
        emails.filter(email => byEmail[email]).forEach(email => {
          const seen = byEmail[email];
          const at = seen.activityDate ? new Date(seen.activityDate) : null;
          statuses[email] = seen.isOnline
            ? {availability: 'online'}
            : {availability: 'offline', lastSeen: at && !isNaN(at.getTime()) ? at.toISOString() : undefined};
        });
        return statuses;
      }));
  }

  onlineCount(conversationId: string, members: MessengerMemberRef[]): Observable<number> {
    return this.statuses(members).pipe(map(statuses =>
      Object.values(statuses).filter(status => status.availability !== 'offline').length));
  }
}
